/**
 * The module is mapped to specification/v2/pages/ObjectPageConfig.ts
 * This comment is maintained as this module is named as ObjectPage.ts and the corresponding module is named as ObjectPageConfig.ts
 */

import { v2 } from '@sap/ux-specification-types';
import {
    pageLayoutInformation,
    childId,
    syncRule,
    validity,
    descriptionSrcURL
} from '../../../common/decoration/decorators';
import { getAppSettingOBJPathV2, getAppRootSettingOBJPath } from '../../application';
import type { FlexibleColumnLayoutType } from '@sap/ux-specification-types';
import { doNotDelete } from '../../../common';

@pageLayoutInformation({
    id: v2.SAPUI5_VIEW_EXTENSION_OBJECT_PAGE,
    pageId: v2.FE_TEMPLATE_V2_OBJECT_PAGE
})
export class ObjectPage implements v2.ObjectPageConfigV2 {
    @syncRule({
        manifest: {
            path: (pageKeys: string[]): string => getAppSettingOBJPathV2(pageKeys)
        }
    })
    @validity({
        since: '1.52.0'
    })
    showConfirmationOnDraftActivate?: boolean;

    @syncRule({
        manifest: {
            path: (pageKeys: string[]): string => getAppSettingOBJPathV2(pageKeys)
        }
    })
    @validity({
        since: '1.52.1'
    })
    hideChevronForUnauthorizedExtNav?: boolean;

    @syncRule({
        manifest: {
            path: (pageKeys: string[]): string => getAppSettingOBJPathV2(pageKeys)
        }
    })
    @validity({
        since: '1.48.0'
    })
    allowDeepLinking?: boolean;

    @syncRule({
        manifest: {
            path: (pageKeys: string[]): string => getAppSettingOBJPathV2(pageKeys)
        }
    })
    @validity({
        since: '1.52.0'
    })
    navToListOnSave?: boolean;

    @childId('header')
    header?: v2.ObjectPageHeader;

    @childId('sections')
    sections?: v2.CustomSections | v2.GenericSections;

    @childId('layout')
    layout?: v2.ObjectPageLayout;

    @syncRule({
        manifest: {
            path: (pageKeys: string[]): string => getAppRootSettingOBJPath(pageKeys)
        }
    })
    @validity({
        since: '1.77.0'
    })
    defaultLayoutTypeIfExternalNavigation?: FlexibleColumnLayoutType.MidColumnFullScreen;

    @syncRule({
        manifest: {
            path: (pageKeys: string[]): string => getAppSettingOBJPathV2(pageKeys),
            import: (value: object): object | undefined => {
                if ('chartSettings' in value) {
                    return value['chartSettings'] as object | undefined;
                }
            },
            key: 'chartSettings'
        }
    })
    @validity({
        since: '1.78.0'
    })
    globalChartSettings?: v2.GlobalChartSettings;

    @syncRule({
        manifest: {
            path: (pageKeys: string[]): string => getAppSettingOBJPathV2(pageKeys),
            import: (value: object): object | undefined => {
                if ('tableSettings' in value) {
                    return value['tableSettings'] as object | undefined;
                }
            },
            key: 'tableSettings',
            delete: doNotDelete
        }
    })
    @validity({
        since: '1.71.0'
    })
    globalTableSettings?: v2.GlobalTableSettings;

    @syncRule({
        manifest: {
            path: (pageKeys: string[]): string => getAppSettingOBJPathV2(pageKeys)
        }
    })
    @validity({
        since: '1.48.5'
    })
    @descriptionSrcURL('https://ui5.sap.com/sdk/#/topic/cfb04f0c58e7409992feb4c91aa9410b')
    createMode?: v2.CreateMode;
}
